import React, { useEffect } from 'react'
import { Redirect } from 'react-router-dom'
import { useAuthStore } from '../store/authStore'

interface AuthGuardProps {
  children: React.ReactNode
}

const AuthGuard: React.FC<AuthGuardProps> = ({ children }) => {
  const { user, loading, supabaseUser, setLoading } = useAuthStore()

  console.log('AuthGuard - State:', { 
    loading, 
    hasSupabaseUser: !!supabaseUser, 
    hasUser: !!user,
    userEmail: user?.email,
    userRole: user?.role,
    onboardingCompleted: user?.onboarding_completed,
    fullUser: user
  })

  // Safety fallback: if we're loading for too long, force stop loading
  useEffect(() => {
    if (loading) {
      const safetyTimeout = setTimeout(() => {
        console.warn('AuthGuard safety timeout: Forcing loading to false after 10 seconds')
        setLoading(false)
      }, 10000) // 10 second safety timeout

      return () => clearTimeout(safetyTimeout)
    }
  }, [loading, setLoading])

  // Show loading if still initializing OR if we have a supabase user but no profile yet
  if (loading || (supabaseUser && !user)) {
    console.log('AuthGuard - Showing loading state')
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500 mx-auto mb-2"></div>
          <p className="text-sm text-gray-600">Loading user data...</p>
          <p className="text-xs text-gray-400 mt-1">Please wait...</p>
        </div>
      </div>
    )
  }

  if (!user) {
    console.log('AuthGuard - No user, redirecting to login')
    return <Redirect to="/login" />
  }

  // Admin users skip onboarding - they don't need the coaching flow
  if (user.role === 'admin') {
    console.log('AuthGuard - Admin user, allowing access')
    return <>{children}</>
  }

  // Redirect to onboarding if user hasn't completed it (coaches and coachees only)
  console.log('AuthGuard - User onboarding status:', user.onboarding_completed)
  console.log('AuthGuard - Full user object:', user)
  
  // Check if onboarding is completed (handle undefined/null as false)
  const onboardingCompleted = user.onboarding_completed === true
  
  if (!onboardingCompleted) {
    console.log('AuthGuard - Onboarding not completed, redirecting to onboarding')
    return <Redirect to="/onboarding" />
  }

  console.log('AuthGuard - User authenticated and onboarded, allowing access')
  return <>{children}</>
}

export default AuthGuard 